import { runInAction } from 'mobx'

import { IPermitTypeField, PermitFieldType } from '~/client/graph'
import { singleInstancesPermitFields } from '~/client/src/shared/constants/PermitFieldTypeConstants'

import PermitType from '../models/PermitType'
import SitePermit from '../models/Workflow'
import PermitWorkflowFieldsStore from '../stores/ui/PermitFieldsStores/PermitWorkflowFields.store'
import { clearSnapshotUrls } from './locationSnapshot'
import { copyObjectDeep } from './util'

// Keyed on field type, not id: ids are minted per template; only single-instance types
// are unambiguous. Location re-derives from the pin; StartFinishDates lives on the entity.
const CARRYABLE_FIELD_TYPES = new Set<PermitFieldType>(
  singleInstancesPermitFields.filter(
    type =>
      type !== PermitFieldType.StartFinishDates &&
      type !== PermitFieldType.Location,
  ),
)

export interface IWorkflowDateSnapshot {
  startDate: number
  endDate: number
  isAllDay: boolean
}

export interface IWorkflowCarryPlan {
  title: string
  dates: IWorkflowDateSnapshot | null
  values: Array<{ type: PermitFieldType; values: unknown[] }>
}

const MS_PER_MINUTE = 60000

function fitsMaxDuration(
  targetType: PermitType,
  startDate: number,
  endDate: number,
): boolean {
  const max = targetType?.maxDurationInMinutes
  if (!targetType?.maxDurationOptions?.isEnabled || !max) {
    return true
  }
  return (endDate - startDate) / MS_PER_MINUTE <= max
}

function findTargetField(
  targetType: PermitType,
  fieldType: PermitFieldType,
): IPermitTypeField {
  return (targetType?.initialStep?.fields ?? []).find(
    field => field?.type === fieldType && field.isShown && !field.parentId,
  )
}

/** Pure, and must run BEFORE the re-seed — that rebuilds `source` in place. */
export function planWorkflowTypeSwitchCarry(
  source: SitePermit,
  targetType: PermitType,
  seedDates: IWorkflowDateSnapshot,
): IWorkflowCarryPlan {
  const plan: IWorkflowCarryPlan = {
    title: source?.title || '',
    dates: null,
    values: [],
  }
  if (!source || !targetType) {
    return plan
  }

  // isFieldChanged is false on every creation (no existingEntity), so diff the seed.
  const userMovedDates =
    !!seedDates &&
    (source.startDate !== seedDates.startDate ||
      source.endDate !== seedDates.endDate ||
      source.isAllDay !== seedDates.isAllDay)
  if (
    userMovedDates &&
    fitsMaxDuration(targetType, source.startDate, source.endDate)
  ) {
    plan.dates = {
      startDate: source.startDate,
      endDate: source.endDate,
      isAllDay: source.isAllDay,
    }
  }

  for (const field of source.fields ?? []) {
    // A conditional child shares its parent's type but has no parent in the target.
    if (!field?.values?.length || field.parentInstanceId) {
      continue
    }
    if (!CARRYABLE_FIELD_TYPES.has(field.type)) {
      continue
    }
    const targetField = findTargetField(targetType, field.type)
    if (!targetField) {
      continue
    }
    const values = copyObjectDeep<unknown[]>(field.values)
    // Report snapshots belong to the old template's capture.
    values.forEach(value => clearSnapshotUrls(value))
    plan.values.push({
      type: field.type,
      values:
        !targetField.isMultiple && values.length > 1 ? [values[0]] : values,
    })
  }
  return plan
}

/** Mutates, and must run AFTER the re-seed. */
export function applyWorkflowTypeSwitchCarry(
  target: SitePermit,
  targetType: PermitType,
  plan: IWorkflowCarryPlan,
  fieldsStore: PermitWorkflowFieldsStore,
): void {
  if (!target || !plan) {
    return
  }
  runInAction(() => {
    if (plan.title) {
      target.title = plan.title
    }
    if (plan.dates) {
      target.startDate = plan.dates.startDate
      target.endDate = plan.dates.endDate
      target.isAllDay = plan.dates.isAllDay
    }
    for (const entry of plan.values) {
      const targetField = findTargetField(targetType, entry.type)
      // Not fields.push: this also prunes values dependent fields' filters now reject.
      if (targetField) {
        fieldsStore.changeMultipleFieldValues(targetField, entry.values)
      }
    }
    // Template-scoped, and nothing else clears it on a swap.
    fieldsStore.recurringSettings = null
    fieldsStore.resetValidationState()
  })
}
